class CustomPickers extends HTMLElement {
  constructor() {
    super();
    this.boundHandleClickOutside = this.handleClickOutside.bind(this);
    this.boundHandleKeydown = this.handleKeydown.bind(this);
    this.boundHandleAddToDrawer = this.handleAddToDrawer.bind(this);
    this.addButton = null;
    this.cartDrawer = null;
  }

  parseEuroToNumber(raw) {
    const str = String(raw ?? '').trim();
    if (!str) return 0;

    const cleaned = str
      .replace('€', '')
      .replace(/\s+/g, '')
      .replace(/,/g, '.')
      .replace(/[^0-9.+-]/g, '');

    const num = Number(cleaned);
    return Number.isFinite(num) ? num : 0;
  }

  formatEuroAbs(num) {
    const value = Number.isFinite(num) ? num : 0;
    return `${value} €`;
  }

  formatEuroDelta(delta) {
    const value = Number.isFinite(delta) ? delta : 0;
    return `${value} €`;
  }

  getSelectedCustomOption(dropdown) {
    if (!dropdown) return null;
    return dropdown.querySelector('[data-option][data-selected]');
  }

  ensureAbsPricesForCustomDropdown(dropdown) {
    if (!dropdown) return;
    const priceEls = dropdown.querySelectorAll('[data-option-price]');

    priceEls.forEach((priceEl) => {
      if (typeof priceEl.dataset.absPrice !== 'undefined') return;
      priceEl.dataset.absPrice = String(this.parseEuroToNumber(priceEl.textContent));
    });
  }

  renderCustomDropdownOptionPricesAbsolute(dropdown) {
    if (!dropdown) return;
    const priceEls = dropdown.querySelectorAll('[data-option-price]');

    priceEls.forEach((priceEl) => {
      const abs = Number(priceEl.dataset.absPrice ?? '0');
      priceEl.textContent = this.formatEuroAbs(abs);
    });
  }

  renderCustomDropdownOptionPricesDelta(dropdown, selectedAbs) {
    if (!dropdown) return;
    const safeSelected = Number.isFinite(selectedAbs) ? selectedAbs : 0;
    const priceEls = dropdown.querySelectorAll('[data-option-price]');

    priceEls.forEach((priceEl) => {
      const abs = Number(priceEl.dataset.absPrice ?? '0');
      const delta = abs - safeSelected;
      priceEl.textContent = this.formatEuroDelta(delta);
    });
  }

  syncCustomDropdown(dropdown) {
    if (!dropdown) return;

    const trigger = dropdown.querySelector('[data-trigger]');
    const labelEl = trigger?.querySelector('[data-label]');
    const labelTextTarget = labelEl?.querySelector('[data-label-text]');

    const selectedOption = this.getSelectedCustomOption(dropdown);
    this.ensureAbsPricesForCustomDropdown(dropdown);
    this.renderCustomDropdownOptionPricesAbsolute(dropdown);

    if (!selectedOption) {
      this.syncTriggerColorSwatch(trigger, null);
      return;
    }

    const selectedPriceEl = selectedOption.querySelector('[data-option-price]');
    const selectedAbs = Number(selectedPriceEl?.dataset?.absPrice ?? '0');

    if (labelTextTarget) {
      const selectedText = selectedOption.querySelector('[data-option-text]')?.textContent?.trim() || '';
      labelTextTarget.textContent = selectedText;
      labelTextTarget.setAttribute('data-value', selectedText);
      this.syncTriggerFont(labelTextTarget, selectedOption);
    }

    this.syncTriggerColorSwatch(trigger, selectedOption);

    this.renderCustomDropdownOptionPricesDelta(dropdown, selectedAbs);
  }

  // Mirrors the selected option's font onto the trigger label, so the closed
  // dropdown shows the same typeface as the option it came from.
  syncTriggerFont(labelTextTarget, selectedOption) {
    const fontFamily = selectedOption?.style.getPropertyValue('--option-font-family').trim();

    if (fontFamily) {
      labelTextTarget.setAttribute('data-font', '');
      labelTextTarget.style.setProperty('--option-font-family', fontFamily);
    } else {
      labelTextTarget.removeAttribute('data-font');
      labelTextTarget.style.removeProperty('--option-font-family');
    }
  }

  syncTriggerColorSwatch(trigger, selectedOption) {
    if (!trigger) return;

    if (selectedOption && selectedOption.hasAttribute('data-color')) {
      const bgColor = selectedOption.style.getPropertyValue('--bg-color').trim();
      trigger.setAttribute('data-color', '');
      if (bgColor) {
        trigger.style.setProperty('--bg-color', bgColor);
      } else {
        trigger.style.removeProperty('--bg-color');
      }
    } else {
      trigger.removeAttribute('data-color');
      trigger.style.removeProperty('--bg-color');
    }
  }

  syncAllCustomDropdowns() {
    const dropdowns = this.querySelectorAll('[data-custom-dropdown]');
    dropdowns.forEach((dropdown) => this.syncCustomDropdown(dropdown));
  }

  // Mirrors the selected option into the trigger label on load. Only the option
  // text, never the price – der Preis steht nur in der aufgeklappten Liste.
  syncMainDropdown(dropdown) {
    if (!dropdown) return;

    const checked = dropdown.querySelector('input[data-input]:checked');
    if (!checked) return;

    const labelEl = dropdown.querySelector('[data-label]');
    const optionEl = dropdown.querySelector(`[data-option][for="${checked.id}"]`);
    if (labelEl && optionEl) {
      labelEl.textContent = this.getOptionLabelText(optionEl);
    }
  }

  // Der Optionstext ohne Preis – fällt auf den kompletten Inhalt zurück, falls
  // eine Option kein [data-option-text] hat.
  getOptionLabelText(optionEl) {
    const textEl = optionEl.querySelector('[data-option-text]');
    return (textEl?.textContent ?? optionEl.textContent).trim();
  }

  syncAllMainDropdowns() {
    const dropdowns = this.querySelectorAll('[data-dropdown]');
    dropdowns.forEach((dropdown) => this.syncMainDropdown(dropdown));
  }

  // The font and the initials colour dropdowns. The font unlocks everything
  // that belongs to the initials: colour and text field.
  getInitialsDropdowns(pickerEl) {
    const customDropdowns = Array.from(pickerEl.querySelectorAll('[data-custom-dropdown]'));
    return [customDropdowns[1], customDropdowns[2]];
  }

  // A dropdown counts as chosen once an option other than "Keine" is selected.
  hasCustomChoice(dropdown) {
    const selected = this.getSelectedCustomOption(dropdown);
    if (!selected) return false;
    const value = (selected.getAttribute('data-value') || '').trim();
    return value !== '' && value.toLowerCase() !== 'keine';
  }

  // Font "Keine" locks the initials colour and the text field. Once a font is
  // chosen both open up, and the add button waits until they are filled in.
  updateCustomInputState(pickerEl) {
    const customInput = pickerEl.querySelector('[data-custom-field] [data-custom-input]');
    if (!customInput) return;

    const [fontDropdown, colorDropdown] = this.getInitialsDropdowns(pickerEl);
    const fontChosen = this.hasCustomChoice(fontDropdown);

    if (colorDropdown) {
      const colorTrigger = colorDropdown.querySelector('[data-trigger]');
      if (colorTrigger) colorTrigger.disabled = !fontChosen;
      colorDropdown.toggleAttribute('data-disabled', !fontChosen);

      // Without a font the colour falls back to "Keine", so nobody pays for
      // an initials colour that has no initials.
      const firstOption = colorDropdown.querySelector('[data-option][data-index="0"]');
      if (!fontChosen && firstOption && this.getSelectedCustomOption(colorDropdown)) {
        colorDropdown.querySelectorAll('[data-option]').forEach((opt) => {
          opt.toggleAttribute('data-selected', opt === firstOption);
        });
        this.syncCustomDropdown(colorDropdown);
      }
    }

    customInput.disabled = !fontChosen;

    // Locking the field must also drop what was typed, otherwise the
    // initials still ship while the font option is "keine".
    if (!fontChosen) customInput.value = '';

    const field = customInput.closest('[data-custom-field]');
    field?.toggleAttribute('data-disabled', !fontChosen);

    this.updateAddButtonState();
  }

  // Font chosen but colour or initials still missing – only counts while the
  // personalisation is switched on.
  hasIncompleteInitials(pickerEl) {
    const toggle = pickerEl.querySelector('[data-customization-toggle]');
    if (!toggle?.checked) return false;

    const [fontDropdown, colorDropdown] = this.getInitialsDropdowns(pickerEl);
    if (!this.hasCustomChoice(fontDropdown)) return false;

    const customInput = pickerEl.querySelector('[data-custom-field] [data-custom-input]');
    const initials = customInput?.value.trim() || '';
    return !this.hasCustomChoice(colorDropdown) || initials === '';
  }

  updateAddButtonState() {
    let blocked = false;

    this.getPickers().forEach((pickerEl) => {
      const incomplete = this.hasIncompleteInitials(pickerEl);
      const hint = pickerEl.querySelector('[data-initials-hint]');
      if (hint) {
        hint.hidden = !incomplete;
        if (!incomplete) hint.removeAttribute('data-error');
      }
      if (incomplete) blocked = true;
    });

    if (blocked) {
      this.disabledButtons();
    } else {
      this.enabledButtons();
    }
  }

  // A click on the blocked button points at what is missing: the hint is
  // emphasised and the first empty initials input gets the focus.
  flagIncompleteInitials() {
    const pickerEl = this.getPickers().find((el) => this.hasIncompleteInitials(el));
    if (!pickerEl) return;

    const hint = pickerEl.querySelector('[data-initials-hint]');
    if (hint) {
      hint.removeAttribute('data-error');
      void hint.offsetWidth; // restart the animation on repeated clicks
      hint.setAttribute('data-error', '');
    }

    const [, colorDropdown] = this.getInitialsDropdowns(pickerEl);

    if (colorDropdown && !this.hasCustomChoice(colorDropdown)) {
      colorDropdown.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Deferred, otherwise the button click reaching handleClickOutside
      // closes the dropdown again right away.
      setTimeout(() => this.openDropdown(colorDropdown));
      return;
    }

    const customInput = pickerEl.querySelector('[data-custom-field] [data-custom-input]');
    if (!customInput) return;
    customInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
    customInput.focus({ preventScroll: true });
  }

  openDropdown(dropdown) {
    this.closeAll();
    dropdown.setAttribute('data-open', '');
    const trigger = dropdown.querySelector('[data-trigger]');
    const panel = dropdown.querySelector('[data-panel]');
    if (trigger) {
      trigger.setAttribute('aria-expanded', 'true');
      trigger.focus({ preventScroll: true });
    }
    if (panel) panel.setAttribute('aria-hidden', 'false');
  }

  // Returns all .custom-picker children (one per product).
  getPickers() {
    return Array.from(this.querySelectorAll('[data-custom-picker]'));
  }

  // Walks up the DOM to find the .custom-picker ancestor of a given element.
  getPickerForElement(el) {
    return el?.closest('[data-custom-picker]') || this.getPickers()[0];
  }

  connectedCallback() {
    this.addEventListener('click', this.handleClick.bind(this));
    this.addEventListener('change', this.handleChange.bind(this));
    document.addEventListener('click', this.boundHandleClickOutside);
    document.addEventListener('keydown', this.boundHandleKeydown);

    this.addButton = document.querySelector('[data-add-to-drawer]');
    if (this.addButton) {
      this.addButton.addEventListener('click', this.boundHandleAddToDrawer);
    }

    this.cartDrawer = document.querySelector('cart-drawer');

    this.querySelectorAll('[data-custom-input]').forEach((input) => {
      input.addEventListener('change', (e) => {
        e.stopImmediatePropagation();
      });
      input.addEventListener('input', () => this.updateAddButtonState());
    });

    this.syncAllMainDropdowns();
    this.syncAllCustomDropdowns();

    this.getPickers().forEach((pickerEl) => {
      this.updateCustomInputState(pickerEl);
      this.initializeCustomizationStateForPicker(pickerEl);
    });

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this._variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, () => {
        this.updateCombinedPrice();
      });
    }
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.boundHandleClickOutside);
    document.removeEventListener('keydown', this.boundHandleKeydown);

    if (this.addButton) {
      this.addButton.removeEventListener('click', this.boundHandleAddToDrawer);
    }

    this._variantChangeUnsubscriber?.();
  }

  // Returns variant-selection dropdowns scoped to a specific picker.
  getDropdownsForPicker(pickerEl) {
    return pickerEl.querySelectorAll('[data-dropdown]');
  }

  // Returns all dropdown containers (variant + custom) across all pickers – used for closeAll.
  getAllDropdownContainers() {
    return this.querySelectorAll('[data-dropdown], [data-custom-dropdown]');
  }

  updateCustomizationDropdownFromCustomSelections(pickerEl) {
    const customizationDropdown = pickerEl.querySelector('[data-dropdown="customization"]');
    if (!customizationDropdown) return;

    const customizationToggle = pickerEl.querySelector('[data-customization-toggle]');
    const isCustomizationEnabled = Boolean(customizationToggle && customizationToggle.checked);

    const inputs = customizationDropdown.querySelectorAll('input[data-input]');
    if (!inputs.length) return;

    let targetIndex = 0;

    if (isCustomizationEnabled) {
      const customDropdowns = pickerEl.querySelectorAll('[data-custom-dropdown]');
      let activeCount = 0;

      customDropdowns.forEach((dropdown) => {
        this.ensureAbsPricesForCustomDropdown(dropdown);

        const options = dropdown.querySelectorAll('[data-option]');
        let selectedIndex = -1;

        options.forEach((option, index) => {
          if (option.hasAttribute('data-selected')) {
            selectedIndex = index;
          }
        });

        if (selectedIndex > 0) {
          const selectedOption = options[selectedIndex];
          const selectedPriceEl = selectedOption?.querySelector('[data-option-price]');
          const selectedAbsPrice = Number(selectedPriceEl?.dataset?.absPrice ?? '0');

          if (selectedAbsPrice > 0) {
            activeCount += 1;
          }
        }
      });

      targetIndex = Math.min(activeCount, inputs.length - 1);
    }

    const targetInput = inputs[targetIndex];

    if (!targetInput || targetInput.checked) return;

    inputs.forEach((input) => {
      input.checked = input === targetInput;
    });

    const changeEvent = new Event('change', { bubbles: true });
    targetInput.dispatchEvent(changeEvent);
  }

  closeAll() {
    this.getAllDropdownContainers().forEach((dropdown) => {
      dropdown.removeAttribute('data-open');
      const trigger = dropdown.querySelector('[data-trigger]');
      const panel = dropdown.querySelector('[data-panel]');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
      if (panel) panel.setAttribute('aria-hidden', 'true');
    });
  }

  getProductVariantsForPicker(pickerEl) {
    const script = pickerEl.querySelector('[data-product-variants]');
    if (!script) return [];

    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      console.error('Failed to parse product variants JSON', error);
      return [];
    }
  }

  getCurrentOptionValuesForPicker(pickerEl) {
    const dropdowns = Array.from(this.getDropdownsForPicker(pickerEl));
    const values = [];

    dropdowns.forEach((dropdown, index) => {
      const checked = dropdown.querySelector('input[data-input]:checked');
      if (checked) {
        values[index] = checked.value;
      }
    });

    if (values.length !== dropdowns.length || values.some((value) => !value)) {
      return null;
    }

    return values;
  }

  getSelectedVariantForPicker(pickerEl) {
    const optionValues = this.getCurrentOptionValuesForPicker(pickerEl);
    if (!optionValues) return null;

    const variants = this.getProductVariantsForPicker(pickerEl);
    if (!variants.length) return null;

    return (
      variants.find((variant) =>
        optionValues.every((value, index) => variant[`option${index + 1}`] === value)
      ) || null
    );
  }

  formatMoney(cents) {
    const currency = window.Shopify?.currency?.active || 'EUR';
    return new Intl.NumberFormat(navigator.language, {
      style: 'currency',
      currency,
      currencyDisplay: 'narrowSymbol',
    }).format(cents / 100);
  }

  updateCombinedPrice() {
    const priceEl = document.querySelector('.price[data-combined-price]');
    if (!priceEl) return;

    const pickers = this.getPickers();
    const skipMainInCart = this.dataset.skipMainInCart === 'true';

    let mainPriceCents = 0;
    let additionalStartIdx = 0;
    if (!skipMainInCart && pickers[0]) {
      const mainVariant = this.getSelectedVariantForPicker(pickers[0]);
      mainPriceCents = mainVariant?.price ?? parseInt(priceEl.dataset.productPrice || '0', 10);
      additionalStartIdx = 1;
    }

    let additionalPriceCents = 0;
    for (let i = additionalStartIdx; i < pickers.length; i++) {
      const variant = this.getSelectedVariantForPicker(pickers[i]);
      if (variant && variant.price) {
        additionalPriceCents += variant.price;
      }
    }

    const combinedCents = mainPriceCents + additionalPriceCents;
    const formatted = this.formatMoney(combinedCents);

    priceEl.querySelectorAll('.price-item--regular, .price-item--sale.price-item--last').forEach((el) => {
      el.textContent = formatted;
    });

    priceEl.dataset.additionalProductPrice = String(additionalPriceCents);
  }

  handleVariantChangeForPicker(pickerEl) {
    const variant = this.getSelectedVariantForPicker(pickerEl);
    if (!variant) return;

    pickerEl.dataset.selectedVariantId = String(variant.id);

    this.updateCombinedPrice();
  }

  disabledButtons() {
    if (this.addButton) {
      this.addButton.setAttribute('aria-disabled', 'true');
      if (this.addButton.parentElement) {
        this.addButton.parentElement.setAttribute('aria-disabled', 'true');
      }
    }
  }

  enabledButtons() {
    if (this.addButton) {
      this.addButton.setAttribute('aria-disabled', 'false');
      if (this.addButton.parentElement) {
        this.addButton.parentElement.setAttribute('aria-disabled', 'false');
      }
    }
  }

  // Builds the cart items array from all pickers and adds them in one request.
  async handleAddToDrawer(event) {
    event.preventDefault();

    if (this.isAddButtonDisabled()) {
      this.flagIncompleteInitials();
      return;
    }

    const items = [];
    const pickers = this.getPickers();
    const skipMainInCart = this.dataset.skipMainInCart === 'true';
    const isBundle = pickers.length >= 2;
    const bundleId = isBundle ? this.generateBundleId() : null;
    const bundleTitle = this.dataset.bundleTitle || '';

    let bundleParentVariantGid = null;
    if (isBundle) {
      const mainVariant = this.getSelectedVariantForPicker(pickers[0]);
      if (mainVariant?.id) {
        bundleParentVariantGid = String(mainVariant.id);
      }
    }

    pickers.forEach((pickerEl, index) => {
      const variant = this.getSelectedVariantForPicker(pickerEl);
      if (!variant?.id) return;

      const item = { id: variant.id, quantity: 1 };
      const properties = {};

      const customizationToggle = pickerEl.querySelector('[data-customization-toggle]');
      const isCustomizationEnabled = Boolean(customizationToggle?.checked);

      if (isCustomizationEnabled) {
        const customField = pickerEl.querySelector('[data-custom-field]');

        pickerEl.querySelectorAll('[data-custom-dropdown]').forEach((dropdown) => {
          const name = dropdown.getAttribute('data-option-name');
          if (!name) return;

          const selectedOption = Array.from(dropdown.querySelectorAll('[data-option]')).find((opt) =>
            opt.hasAttribute('data-selected')
          );
          if (!selectedOption) return;

          if (selectedOption.getAttribute('data-index') === '0') return;

          const value = (selectedOption.dataset.value || '').trim();
          if (value) properties[name] = value;
        });

        const customInput = customField?.querySelector('[data-custom-input]');
        const customInputLegend = customField?.querySelector('[data-option-name]');

        if (customInput && customInputLegend && !customField.hasAttribute('data-disabled')) {
          const inputName = customInputLegend.textContent.trim();
          const inputValue = customInput.value.trim();

          if (inputName && inputValue) {
            properties[inputName] = `${inputValue},`;
          }
        }
      }

      if (bundleId) {
        properties._bundle_id = bundleId;
        properties._bundle_role = skipMainInCart
          ? `extra_${index + 1}`
          : (index === 0 ? 'main' : `extra_${index}`);
        if (bundleParentVariantGid) {
          properties._bundle_parent_variant_id = bundleParentVariantGid;
        }
        if (bundleTitle) {
          properties._bundle_title = bundleTitle;
        }
      }

      if (Object.keys(properties).length) item.properties = properties;
      items.push(item);
    });

    if (!items.length) return;

    await this.addToCart(items);
  }

  generateBundleId() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    return `bundle-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }

  isAddButtonDisabled() {
    return this.addButton && this.addButton.getAttribute('aria-disabled') === 'true';
  }

  async addToCart(items) {
    const formData = { items };
    this.disabledButtons();
    try {
      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      const data = await response.json();
      await this.updateCartDrawer();
      await this.cartDrawer.open();
      console.log(data);
    } catch (error) {
      console.error('Error adding to cart:', error);
    } finally {
      this.updateAddButtonState();
    }
  }

  async updateCartDrawer() {
    const newHTML = await this.fetchSection();
    const updatedCart = new DOMParser().parseFromString(newHTML, 'text/html').querySelector('.cart-drawer__inner');

    const currentContent = document.querySelector('.cart-drawer__inner');
    if (updatedCart && currentContent && currentContent.innerHTML !== updatedCart.innerHTML) {
      currentContent.innerHTML = updatedCart.innerHTML;
      this.cartDrawer.classList.remove('is-empty');
    }
  }

  async fetchSection() {
    const response = await fetch(window.Shopify.routes.root + '?section_id=cart-drawer');
    return response.text();
  }

  handleClick(e) {
    const customOption = e.target.closest('[data-custom-dropdown] [data-option]');
    if (customOption && this.contains(customOption)) {
      const dropdown = customOption.closest('[data-custom-dropdown]');
      if (!dropdown) return;

      const trigger = dropdown.querySelector('[data-trigger]');
      const panel = dropdown.querySelector('[data-panel]');

      if (trigger && typeof customOption.dataset.value !== 'undefined') {
        trigger.dataset.value = customOption.dataset.value;
      }

      const options = dropdown.querySelectorAll('[data-option]');
      options.forEach((opt) => {
        if (opt === customOption) {
          opt.setAttribute('data-selected', '');
        } else {
          opt.removeAttribute('data-selected');
        }
      });

      this.syncCustomDropdown(dropdown);

      const pickerEl = this.getPickerForElement(customOption);
      this.updateCustomInputState(pickerEl);
      this.updateCustomizationDropdownFromCustomSelections(pickerEl);

      dropdown.removeAttribute('data-open');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
      if (panel) panel.setAttribute('aria-hidden', 'true');

      e.stopPropagation();
      return;
    }

    const trigger = e.target.closest('[data-trigger]');
    if (!trigger) return;

    const dropdown = trigger.closest('[data-dropdown], [data-custom-dropdown]');
    if (!dropdown || !this.contains(dropdown)) return;

    e.stopPropagation();

    const isOpen = dropdown.hasAttribute('data-open');
    this.closeAll();

    if (!isOpen) {
      dropdown.setAttribute('data-open', '');
      trigger.setAttribute('aria-expanded', 'true');
      const panel = dropdown.querySelector('[data-panel]');
      if (panel) panel.setAttribute('aria-hidden', 'false');
    }
  }

  handleChange(e) {
    const input = e.target;

    if (input.matches('[data-customization-toggle]')) {
      const pickerEl = this.getPickerForElement(input);
      this.handleCustomizationToggleChange(input, pickerEl);
      this.updateAddButtonState();
      return;
    }

    if (!input.hasAttribute('data-input')) return;

    const dropdown = input.closest('[data-dropdown]');
    const labelEl = dropdown?.querySelector('[data-label]');
    const optionEl = dropdown?.querySelector(`[data-option][for="${input.id}"]`);

    if (labelEl && optionEl) {
      labelEl.textContent = this.getOptionLabelText(optionEl);
    }

    const pickerEl = this.getPickerForElement(input);
    this.handleVariantChangeForPicker(pickerEl);
    this.closeAll();
  }

  initializeCustomizationStateForPicker(pickerEl) {
    const customizationToggle = pickerEl.querySelector('[data-customization-toggle]');
    const customizationDropdown = pickerEl.querySelector('[data-dropdown="customization"]');

    if (!customizationDropdown || !customizationToggle) return;

    const inputs = customizationDropdown.querySelectorAll('input[data-input]');
    if (!inputs.length) return;

    if (!customizationToggle.checked) {
      const targetInput = inputs[0];
      if (!targetInput) return;

      if (!targetInput.checked) {
        inputs.forEach((input) => {
          input.checked = input === targetInput;
        });

        const changeEvent = new Event('change', { bubbles: true });
        targetInput.dispatchEvent(changeEvent);
      }
    } else {
      this.updateCustomizationDropdownFromCustomSelections(pickerEl);
    }
  }

  handleCustomizationToggleChange(toggle, pickerEl, skipSync = false) {
    const customizationDropdown = pickerEl.querySelector('[data-dropdown="customization"]');
    if (!customizationDropdown) return;

    const inputs = customizationDropdown.querySelectorAll('input[data-input]');
    if (!inputs.length) return;

    if (!toggle.checked) {
      const targetInput = inputs[0];
      if (targetInput && !targetInput.checked) {
        inputs.forEach((input) => {
          input.checked = input === targetInput;
        });
        const changeEvent = new Event('change', { bubbles: true });
        targetInput.dispatchEvent(changeEvent);
      }
    } else {
      this.updateCustomizationDropdownFromCustomSelections(pickerEl);
    }

    const isDesktop = window.matchMedia('(min-width: 750px)').matches;
    if (!skipSync && isDesktop) {
      const allPickers = this.getPickers();
      const idx = allPickers.indexOf(pickerEl);
      if (idx !== -1) {
        const pairIdx = idx % 2 === 0 ? idx + 1 : idx - 1;
        const pairPicker = allPickers[pairIdx];
        const pairToggle = pairPicker?.querySelector('[data-customization-toggle]');
        if (pairToggle && pairToggle.checked !== toggle.checked) {
          pairToggle.checked = toggle.checked;
          this.handleCustomizationToggleChange(pairToggle, pairPicker, true);
        }
      }
    }
  }

  handleClickOutside(e) {
    if (!this.contains(e.target)) {
      this.closeAll();
    }
  }

  handleKeydown(e) {
    if (e.key === 'Escape') {
      this.closeAll();
    }
  }
}

if (!customElements.get('custom-pickers')) {
  customElements.define('custom-pickers', CustomPickers);
}


if (!customElements.get('picker-sidebar')) {
  class PickerSidebar extends HTMLElement {
    constructor() {
      super();
      this.closeButton = this.querySelector('[data-close]');
      this.aside = this.querySelector('[data-aside]');
      this.backdrop = this.querySelector('[data-backdrop]');
      this.backButton = this.querySelector('[data-back]');
      this.items = this.querySelectorAll('[data-item]');

      this.lightbox = this.querySelector('[data-lightbox]');
      this.lightboxImage = this.querySelector('[data-lightbox-image]');
      this.lightboxCloseButtons = this.querySelectorAll('[data-lightbox-close]');
      this.lightboxBackdrop = this.querySelector('[data-lightbox-backdrop]');
      this.lightboxTriggers = this.querySelectorAll('[data-lightbox-trigger]');

      this.sidebarId = this.getAttribute('data-sidebar-id');
      this.boundHandleTriggerClick = this.handleTriggerClick.bind(this);
    }

    connectedCallback() {
      this.setupListeners();
    }

    lockBodyScroll() {
      const body = document.body;
      if (!body) return;
      if (body.hasAttribute('data-picker-sidebar-open')) return;

      const computed = window.getComputedStyle(body);
      const currentPaddingRight = Number.parseFloat(computed.paddingRight || '0') || 0;
      const scrollbarWidth = Math.max(0, window.innerWidth - document.documentElement.clientWidth);

      body.dataset.pickerSidebarPrevOverflow = body.style.overflow || '';
      body.dataset.pickerSidebarPrevPaddingRight = body.style.paddingRight || '';
      body.dataset.pickerSidebarPrevScrollY = String(window.scrollY || 0);
      body.dataset.pickerSidebarComputedPaddingRight = String(currentPaddingRight);
      body.dataset.pickerSidebarScrollbarWidth = String(scrollbarWidth);

      body.setAttribute('data-picker-sidebar-open', '');

      if (scrollbarWidth > 0) {
        body.style.paddingRight = `${currentPaddingRight + scrollbarWidth}px`;
      }

      body.style.overflow = 'hidden';
    }

    unlockBodyScroll() {
      const body = document.body;
      if (!body) return;
      if (!body.hasAttribute('data-picker-sidebar-open')) return;

      body.removeAttribute('data-picker-sidebar-open');

      body.style.overflow = body.dataset.pickerSidebarPrevOverflow || '';
      body.style.paddingRight = body.dataset.pickerSidebarPrevPaddingRight || '';

      delete body.dataset.pickerSidebarPrevOverflow;
      delete body.dataset.pickerSidebarPrevPaddingRight;
      delete body.dataset.pickerSidebarPrevScrollY;
      delete body.dataset.pickerSidebarComputedPaddingRight;
      delete body.dataset.pickerSidebarScrollbarWidth;
    }

    setupListeners() {
      this.setupCloseButtonListener();
      this.setupAsideClickListener();
      this.setupBackdropClickListener();
      this.setupBackButtonListener();
      this.setupTriggerListeners();
      this.setupLightboxListeners();
      this.setupEscapeKeyListener();
    }

    setupCloseButtonListener() {
      if (this.closeButton) {
        this.closeButton.addEventListener('click', () => {
          this.closeSidebar();
        });
      }
    }

    setupAsideClickListener() {
      if (this.aside) {
        this.aside.addEventListener('click', (event) => {
          event.stopPropagation();
        });
      }
    }

    setupBackdropClickListener() {
      if (this.backdrop) {
        this.backdrop.addEventListener('click', () => {
          if (this.hasAttribute('data-open')) {
            this.closeSidebar();
          }
        });
      }
    }

    setupBackButtonListener() {
      if (!this.backButton) return;
      this.backButton.addEventListener('click', () => {
        this.closeSidebar();
      });
    }

    // Delegated on document: the trigger inside <variant-selects> gets replaced
    // on every variant change, so listeners bound to the node itself are lost.
    setupTriggerListeners() {
      document.addEventListener('click', this.boundHandleTriggerClick);
    }

    getSidebarTriggers() {
      const selector = this.sidebarId
        ? `[data-sidebar-trigger][data-sidebar-id="${CSS.escape(this.sidebarId)}"]`
        : '[data-sidebar-trigger]';
      return Array.from(document.querySelectorAll(selector));
    }

    handleTriggerClick(event) {
      const trigger = event.target instanceof Element ? event.target.closest('[data-sidebar-trigger]') : null;
      if (!trigger) return;
      if (this.sidebarId && trigger.getAttribute('data-sidebar-id') !== this.sidebarId) return;

      const rawIndex =
        trigger.getAttribute('data-index') ??
        trigger.getAttribute('data-sidebar-index') ??
        trigger.dataset.index ??
        trigger.dataset.sidebarIndex;

      const parsed = rawIndex != null ? Number.parseInt(String(rawIndex), 10) : NaN;
      const index = Number.isFinite(parsed) ? parsed : Math.max(0, this.getSidebarTriggers().indexOf(trigger));

      this.openSidebar(index);
    }

    setupLightboxListeners() {
      if (this.lightboxTriggers && this.lightboxTriggers.length) {
        this.lightboxTriggers.forEach((trigger) => {
          trigger.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            const src = trigger.getAttribute('data-lightbox-src');
            const srcset = trigger.getAttribute('data-lightbox-srcset') || '';
            const alt = trigger.getAttribute('data-lightbox-alt') || '';
            this.openLightbox(src, alt, srcset);
          });
        });
      }

      if (this.lightboxCloseButtons && this.lightboxCloseButtons.length) {
        this.lightboxCloseButtons.forEach((button) => {
          button.addEventListener('click', (event) => {
            event.stopPropagation();
            this.closeLightbox();
          });
        });
      }

      if (this.lightboxBackdrop) {
        this.lightboxBackdrop.addEventListener('click', (event) => {
          event.stopPropagation();
          this.closeLightbox();
        });
      }
    }

    preloadLightboxImages(index) {
      if (!this.items || !this.items.length) return;
      const item = this.items[index];
      if (!item) return;
      const triggers = item.querySelectorAll('[data-lightbox-trigger]');
      triggers.forEach((trigger) => {
        if (trigger.dataset.lightboxPreloaded === 'true') return;
        const src = trigger.getAttribute('data-lightbox-src');
        const srcset = trigger.getAttribute('data-lightbox-srcset');
        if (!src) return;
        const img = new Image();
        if (srcset) img.srcset = srcset;
        img.src = src;
        trigger.dataset.lightboxPreloaded = 'true';
      });
    }

    openLightbox(src, alt, srcset) {
      if (!this.lightbox || !src) return;
      if (this.lightboxImage) {
        if (srcset) {
          this.lightboxImage.setAttribute('srcset', srcset);
        } else {
          this.lightboxImage.removeAttribute('srcset');
        }
        this.lightboxImage.setAttribute('src', src);
        this.lightboxImage.setAttribute('alt', alt || '');
      }
      this.lightbox.setAttribute('data-open', '');
      this.lightbox.setAttribute('aria-hidden', 'false');
    }

    closeLightbox() {
      if (!this.lightbox) return;
      this.lightbox.removeAttribute('data-open');
      this.lightbox.setAttribute('aria-hidden', 'true');
    }

    isLightboxOpen() {
      return Boolean(this.lightbox && this.lightbox.hasAttribute('data-open'));
    }

    setupEscapeKeyListener() {
      document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        if (!this.hasAttribute('data-open')) return;
        if (this.isLightboxOpen()) {
          this.closeLightbox();
          return;
        }
        this.closeSidebar();
      });
    }

    openSidebar(index = 0) {
      const safeIndex = Math.max(0, Math.min(index, this.items.length - 1));
      this.setAttribute('data-open', '');
      this.lockBodyScroll();

      this.items.forEach((item, i) => {
        if (i === safeIndex) {
          item.setAttribute('data-active', '');
        } else {
          item.removeAttribute('data-active');
        }
      });

      this.preloadLightboxImages(safeIndex);
    }

    closeSidebar() {
      this.closeLightbox();
      this.removeAttribute('data-open');
      this.items.forEach((item) => item.removeAttribute('data-active'));
      this.unlockBodyScroll();
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.boundHandleTriggerClick);
    }

  }
  customElements.define('picker-sidebar', PickerSidebar);
}
